import { useEffect, useState, useCallback } from 'react';
import {
  getVapidPublicKey,
  postPushSubscription,
  deletePushSubscription,
} from '../api/push';

// Story 066 — Web Push subscription state for the player surface.
// Mirrors useInstall (lib/pwa/install.ts): a single shared hook used by the
// banner and the user menu. localStorage flag for banner dismissal — the
// menu entry is the recovery path.

const DISMISS_KEY = 'swissmagic.push.dismissed';

type PermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

function readPermission(): PermissionState {
  if (typeof Notification === 'undefined') return 'unsupported';
  if (!('PushManager' in window)) return 'unsupported';
  return Notification.permission as PermissionState;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  if ((window.navigator as { standalone?: boolean }).standalone === true) return true;
  return false;
}

function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

function readDismissed(): boolean {
  if (typeof localStorage === 'undefined') return false;
  try {
    return localStorage.getItem(DISMISS_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeDismissed(v: boolean): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (v) localStorage.setItem(DISMISS_KEY, 'true');
    else localStorage.removeItem(DISMISS_KEY);
  } catch {
    /* storage disabled / quota — best effort */
  }
}

// Convert a base64url string (the VAPID public key shape) to the Uint8Array
// the PushManager.subscribe() API expects.
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

function arrayBufferToBase64Url(buf: ArrayBuffer | null): string {
  if (!buf) return '';
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export interface PushState {
  // Browser-level permission state. 'unsupported' means PushManager / Notification
  // aren't available on this platform.
  permission: PermissionState;
  // True when the page is launched standalone (PWA installed). On iOS this
  // is required for Web Push to work at all.
  installed: boolean;
  // True on iOS (any browser).
  ios: boolean;
  // True when the user dismissed the persistent enable-notifications banner.
  // Affects banner visibility only — the user-menu entry is unaffected.
  dismissed: boolean;
  // True when there is a live PushManager subscription in this browser.
  subscribed: boolean;
}

function readBaseState(): Omit<PushState, 'subscribed'> {
  return {
    permission: readPermission(),
    installed: isStandalone(),
    ios: isIos(),
    dismissed: readDismissed(),
  };
}

async function readSubscribed(): Promise<boolean> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return false;
  }
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) return false;
    const sub = await reg.pushManager.getSubscription();
    return sub !== null;
  } catch {
    return false;
  }
}

export type EnableResult =
  | 'enabled'
  | 'permission-denied'
  | 'ios-needs-install'
  | 'unsupported'
  | 'no-vapid-key'
  | 'error';

export type DisableResult = 'disabled' | 'not-subscribed' | 'error';

// Module-level subscriber registry for cross-instance state sync. When
// one usePushSubscription() instance enables/disables, every other
// instance (e.g. AppHeader and EnableNotificationsBanner both call the
// hook) must re-read state too — otherwise the user-menu lags one
// page-refresh behind the banner.
//
// Same pattern install.ts uses for the same reason.
const subscribers = new Set<() => void>();
function notifyAll() {
  for (const cb of subscribers) cb();
}

async function doEnable(): Promise<EnableResult> {
  if (typeof Notification === 'undefined' || !('PushManager' in window)) {
    return 'unsupported';
  }
  if (isIos() && !isStandalone()) return 'ios-needs-install';
  let perm = Notification.permission;
  if (perm === 'default') {
    perm = await Notification.requestPermission();
  }
  if (perm !== 'granted') return 'permission-denied';

  const publicKey = await getVapidPublicKey();
  if (!publicKey) return 'no-vapid-key';

  const reg = await navigator.serviceWorker.ready;
  const existing = await reg.pushManager.getSubscription();
  // Cast: TS 5.5+ tightened BufferSource to strictly ArrayBuffer-backed,
  // but Uint8Array's .buffer is ArrayBufferLike — the PushManager spec
  // accepts both. Runtime is fine; sidestep the over-strict type.
  const subscription =
    existing ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(
        publicKey,
      ) as unknown as BufferSource,
    }));

  const json = subscription.toJSON() as {
    endpoint?: string;
    keys?: { p256dh?: string; auth?: string };
  };
  const p256dh = json.keys?.p256dh ??
    arrayBufferToBase64Url(subscription.getKey('p256dh'));
  const auth = json.keys?.auth ??
    arrayBufferToBase64Url(subscription.getKey('auth'));
  if (!json.endpoint || !p256dh || !auth) return 'error';

  await postPushSubscription({
    endpoint: json.endpoint,
    keys: { p256dh, auth },
    userAgent:
      typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  });
  return 'enabled';
}

async function doDisable(): Promise<DisableResult> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return 'not-subscribed';
  }
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return 'not-subscribed';
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return 'not-subscribed';
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch {
    /* still try to delete server-side */
  }
  try {
    await deletePushSubscription(endpoint);
  } catch {
    return 'error';
  }
  return 'disabled';
}

export function usePushSubscription(): PushState & {
  enable: () => Promise<EnableResult>;
  disable: () => Promise<DisableResult>;
  dismissBanner: () => void;
} {
  const [base, setBase] = useState<Omit<PushState, 'subscribed'>>(readBaseState);
  const [subscribed, setSubscribed] = useState(false);

  const refresh = useCallback(async () => {
    setBase(readBaseState());
    setSubscribed(await readSubscribed());
  }, []);

  useEffect(() => {
    void refresh();
    subscribers.add(refresh);
    return () => {
      subscribers.delete(refresh);
    };
  }, [refresh]);

  const enable = useCallback(async (): Promise<EnableResult> => {
    const r = await doEnable();
    notifyAll();
    return r;
  }, []);

  const disable = useCallback(async (): Promise<DisableResult> => {
    const r = await doDisable();
    notifyAll();
    return r;
  }, []);

  const dismissBanner = useCallback(() => {
    writeDismissed(true);
    notifyAll();
  }, []);

  return { ...base, subscribed, enable, disable, dismissBanner };
}
